import { Layout } from "@/components/Layout";
import { SEO } from "@/components/SEO";
import { Breadcrumb } from "@/components/Breadcrumb";
import { StructuredData } from "@/components/StructuredData";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DrawCeremony } from "@/components/DrawCeremony";
import { Ticket, Gift, Sparkles } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import drawingsImage from "@/assets/drawings-hero.jpg";

const LotteryNumbers = () => {
  const [numbersCount, setNumbersCount] = useState("6");
  const [maxNumber, setMaxNumber] = useState("49");
  const [results, setResults] = useState<number[]>([]);

  const generate = () => {
    const count = parseInt(numbersCount);
    const max = parseInt(maxNumber);

    if (isNaN(count) || isNaN(max) || count < 1 || count > max || max > 100) {
      toast.error("Invalid input");
      return;
    }

    const numbers = new Set<number>();
    const array = new Uint32Array(count * 3);
    crypto.getRandomValues(array);
    
    let i = 0;
    while (numbers.size < count && i < array.length) {
      const num = (array[i] % max) + 1;
      numbers.add(num);
      i++;
    }

    const result = Array.from(numbers).sort((a, b) => a - b);
    setResults(result);
    toast.success("Lottery numbers generated!");
  };

  return (
    <Card className="p-6 shadow-[var(--shadow-medium)]">
      <div className="flex items-center gap-2 mb-6">
        <Ticket className="w-6 h-6 text-primary" />
        <h2 className="text-2xl font-bold">Lottery Numbers</h2>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-6">
        <div>
          <Label htmlFor="count">How many numbers?</Label>
          <Input
            id="count"
            type="number"
            value={numbersCount}
            onChange={(e) => setNumbersCount(e.target.value)}
            min="1"
            max="20"
            className="mt-1"
          />
        </div>
        <div>
          <Label htmlFor="max">Highest number?</Label>
          <Input
            id="max"
            type="number"
            value={maxNumber}
            onChange={(e) => setMaxNumber(e.target.value)}
            min="1"
            max="100"
            className="mt-1"
          />
        </div>
      </div>

      <Button onClick={generate} className="w-full mb-4">
        Generate Lottery Numbers
      </Button>

      {results.length > 0 && (
        <div className="flex flex-wrap gap-3 justify-center">
          {results.map((num, i) => (
            <div
              key={i}
              className="w-14 h-14 rounded-full bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center text-2xl font-bold text-primary-foreground shadow-[var(--shadow-medium)]"
            >
              {num}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};

const RaffleDrawing = () => {
  const [participants, setParticipants] = useState("");
  const [winnersCount, setWinnersCount] = useState("1");
  const [winners, setWinners] = useState<string[]>([]);

  const draw = () => {
    const items = participants.split("\n").filter((item) => item.trim());
    const count = parseInt(winnersCount);

    if (items.length < count) {
      toast.error("Not enough participants");
      return;
    }

    const selected = new Set<string>();
    const array = new Uint32Array(count * 3);
    crypto.getRandomValues(array);

    let i = 0;
    while (selected.size < count && i < array.length) {
      const index = array[i] % items.length;
      selected.add(items[index]);
      i++;
    }

    setWinners(Array.from(selected));
    toast.success("Winners drawn!");
  };

  return (
    <Card className="p-6 shadow-[var(--shadow-medium)]">
      <div className="flex items-center gap-2 mb-6">
        <Gift className="w-6 h-6 text-accent" />
        <h2 className="text-2xl font-bold">Raffle Drawing</h2>
      </div>

      <div className="mb-4">
        <Label htmlFor="participants">Participants (one per line)</Label>
        <textarea
          id="participants"
          value={participants}
          onChange={(e) => setParticipants(e.target.value)}
          placeholder="John Doe&#10;Jane Smith&#10;Bob Johnson"
          rows={6}
          className="w-full mt-1 p-2 rounded-md border border-input bg-background font-mono text-sm"
        />
      </div>

      <div className="mb-4">
        <Label htmlFor="winners-count">Number of winners</Label>
        <Input
          id="winners-count"
          type="number"
          value={winnersCount}
          onChange={(e) => setWinnersCount(e.target.value)}
          min="1"
          max="100"
          className="mt-1"
        />
      </div>

      <Button onClick={draw} className="w-full mb-4">
        Draw Winners
      </Button>

      {winners.length > 0 && (
        <div className="bg-secondary rounded-lg p-4">
          <p className="text-sm text-muted-foreground mb-2">Winners:</p>
          <ol className="list-decimal list-inside space-y-2">
            {winners.map((winner, i) => (
              <li key={i} className="text-lg font-semibold text-foreground">
                {winner}
              </li>
            ))}
          </ol>
        </div>
      )}
    </Card>
  );
};

const Drawings = () => {
  const breadcrumbs = [
    { label: "Drawings", href: "/drawings" }
  ];

  const [showCeremony, setShowCeremony] = useState(false);
  const [ceremonyRange, setCeremonyRange] = useState({ min: 1, max: 100 });

  return (
    <Layout>
      <SEO 
        title="Lottery Number Generator & Raffle Drawings - Live Draw Ceremony"
        description="Generate lottery numbers and conduct fair raffle drawings with live ceremony mode. Cryptographically secure, shareable results, and downloadable certificates. Perfect for tombola, raffles, and prize drawings."
        canonical="https://numbers-generator.com/drawings"
        keywords="lottery number generator, raffle drawing, tombola, live draw, lottery picker, raffle numbers, prize drawing, winning numbers, fair raffle"
      />
      <StructuredData 
        type="BreadcrumbList"
        breadcrumbs={[
          { name: "Home", url: "https://numbers-generator.com/" },
          { name: "Drawings", url: "https://numbers-generator.com/drawings" }
        ]}
      />
      <div className="container mx-auto px-4 py-12">
        <Breadcrumb items={breadcrumbs} />
        <header className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-accent/10 text-accent px-4 py-2 rounded-full mb-4 text-sm font-medium">
            <Sparkles className="w-4 h-4" />
            NEW: Live Draw Ceremony Mode
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
            RandomGen Drawings & Raffles
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Professional raffle draws with live ceremony, shareable results, and proof certificates
          </p>
        </header>

        {/* Live Draw Ceremony */}
        <div className="max-w-4xl mx-auto mb-12">
          <Card className="p-6 bg-gradient-to-br from-primary/5 to-accent/5 border-2 border-primary/20">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">Live Draw Ceremony</h2>
                <p className="text-sm text-muted-foreground">
                  Dramatic reveal • Shareable link • Proof certificate • Multi-viewer support
                </p>
              </div>
            </div>

            {!showCeremony ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="ceremony-min">Minimum Number</Label>
                    <Input
                      id="ceremony-min"
                      type="number"
                      value={ceremonyRange.min}
                      onChange={(e) => setCeremonyRange(prev => ({ ...prev, min: parseInt(e.target.value) || 1 }))}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="ceremony-max">Maximum Number</Label>
                    <Input
                      id="ceremony-max"
                      type="number"
                      value={ceremonyRange.max}
                      onChange={(e) => setCeremonyRange(prev => ({ ...prev, max: parseInt(e.target.value) || 100 }))}
                      className="mt-1"
                    />
                  </div>
                </div>
                <Button 
                  onClick={() => setShowCeremony(true)}
                  className="w-full gap-2 text-lg py-6"
                  size="lg"
                >
                  <Sparkles className="w-5 h-5" />
                  Start Live Ceremony Mode
                </Button>
                <p className="text-xs text-center text-muted-foreground">
                  Perfect for tombola, raffles, and prize drawings with multiple viewers
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                <DrawCeremony 
                  minValue={ceremonyRange.min}
                  maxValue={ceremonyRange.max}
                />
                <Button 
                  variant="outline"
                  onClick={() => setShowCeremony(false)}
                  className="w-full"
                >
                  Back to Setup
                </Button>
              </div>
            )}
          </Card>
        </div>

        <div className="text-center mb-8">
          <h2 className="text-2xl font-semibold mb-2">Quick Tools</h2>
          <p className="text-muted-foreground">Simple lottery and raffle generators</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-6xl mx-auto mb-12">
          <LotteryNumbers />
          <RaffleDrawing />
        </div>

        {/* Drawings Hero Image at Bottom */}
        <div className="rounded-2xl overflow-hidden shadow-2xl max-w-5xl mx-auto">
          <img 
            src={drawingsImage} 
            alt="Colorful lottery balls with numbers and raffle tickets - cryptographically secure lottery number generator and fair raffle drawing tool for prize giveaways"
            className="w-full h-auto object-cover"
            loading="lazy"
          />
        </div>
      </div>
    </Layout>
  );
};

export default Drawings;
